import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Roles } from '@tips-sso/shared';
import { PageHeader } from '../components/PageHeader.js';
import { CustomerTabs } from '../components/CustomerTabs.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Modal } from '../components/Modal.js';
import { Loading, ErrorState } from '../components/States.js';
import { Field, StatusBadge } from '../components/Field.js';
import { useAuth } from '../auth/AuthContext.js';
import { errorMessage } from '../api/client.js';
import {
  createMicrosoftSender,
  fetchMicrosoftConsent,
  fetchMicrosoftSenders,
  startMicrosoftConsent,
  type CreateSenderInput,
  type MicrosoftSender,
} from '../api/endpoints.js';

const SENDER_TYPES: CreateSenderInput['senderType'][] = [
  'user_mailbox',
  'shared_mailbox',
  'service_mailbox',
];

export function CustomerMicrosoftPage() {
  const { id: customerOrgId = '' } = useParams();
  const qc = useQueryClient();
  const { hasAnyRole } = useAuth();
  const [open, setOpen] = useState(false);

  // Only a customer admin / consent admin may approve consent or add senders;
  // SalesOps/reseller managers get a read-only view (enforced server-side too).
  const canManage = hasAnyRole([Roles.MicrosoftConsentAdmin, Roles.CustomerAdmin]);

  const consent = useQuery({
    queryKey: ['msConsent', customerOrgId],
    queryFn: () => fetchMicrosoftConsent(customerOrgId),
  });
  const senders = useQuery({
    queryKey: ['msSenders', customerOrgId],
    queryFn: () => fetchMicrosoftSenders(customerOrgId),
  });

  const startConsent = useMutation({
    mutationFn: () => startMicrosoftConsent(customerOrgId),
    onSuccess: (res) => {
      if (res.consentUrl) window.location.assign(res.consentUrl);
    },
  });

  const senderColumns: Column<MicrosoftSender>[] = [
    { key: 'email', header: 'Sender', render: (s) => s.sender_email },
    { key: 'name', header: 'Display name', render: (s) => s.sender_display_name ?? '—' },
    { key: 'type', header: 'Type', render: (s) => <span className="badge">{s.sender_type}</span> },
    { key: 'status', header: 'Status', render: (s) => <StatusBadge status={s.status} /> },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Microsoft 365"
        subtitle={`Graph consent & sender identities — Org ${customerOrgId}`}
        actions={
          canManage && (
            <button
              type="button"
              className="btn btn--primary"
              disabled={startConsent.isPending}
              onClick={() => startConsent.mutate()}
            >
              {startConsent.isPending ? 'Starting…' : 'Start consent'}
            </button>
          )
        }
      />
      <CustomerTabs customerOrgId={customerOrgId} />

      {!canManage && (
        <div className="notice notice--warning">
          Read-only view. Microsoft consent can only be approved by a customer admin; resellers and
          SalesOps may view status but cannot configure it.
        </div>
      )}
      {startConsent.isError && (
        <div className="form-error">{errorMessage(startConsent.error)}</div>
      )}

      <section className="card">
        <h3 className="card__title">Consent status</h3>
        {consent.isLoading ? (
          <Loading />
        ) : consent.isError || !consent.data ? (
          <ErrorState error={consent.error} onRetry={() => void consent.refetch()} />
        ) : (
          <dl className="kv">
            <dt>Status</dt>
            <dd>
              <StatusBadge status={consent.data.consent_status} />
            </dd>
            <dt>Azure tenant</dt>
            <dd>{consent.data.azure_tenant_id ?? '—'}</dd>
            <dt>Tenant name</dt>
            <dd>{consent.data.tenant_display_name ?? '—'}</dd>
            <dt>Consent type</dt>
            <dd>{consent.data.consent_type ?? '—'}</dd>
            <dt>Consented by</dt>
            <dd>{consent.data.consented_by_email ?? '—'}</dd>
            <dt>Consented at</dt>
            <dd>
              {consent.data.consented_at
                ? new Date(consent.data.consented_at).toLocaleString()
                : '—'}
            </dd>
          </dl>
        )}
      </section>

      <section className="card">
        <div className="card__title-row">
          <h3 className="card__title">Sender identities</h3>
          {canManage && (
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setOpen(true)}>
              Add sender
            </button>
          )}
        </div>
        <DataTable
          columns={senderColumns}
          rows={senders.data}
          rowKey={(s) => s.id}
          isLoading={senders.isLoading}
          isError={senders.isError}
          error={senders.error}
          onRetry={() => void senders.refetch()}
          emptyMessage="No sender identities configured."
        />
      </section>

      <AddSenderModal
        customerOrgId={customerOrgId}
        open={open}
        onClose={() => setOpen(false)}
        onCreated={() => {
          setOpen(false);
          void qc.invalidateQueries({ queryKey: ['msSenders', customerOrgId] });
        }}
      />
    </div>
  );
}

function AddSenderModal({
  customerOrgId,
  open,
  onClose,
  onCreated,
}: {
  customerOrgId: string;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState<Omit<CreateSenderInput, 'orgId'>>({
    senderEmail: '',
    senderType: 'user_mailbox',
  });
  const mutation = useMutation({
    mutationFn: () => createMicrosoftSender({ orgId: customerOrgId, ...form }),
    onSuccess: onCreated,
  });

  return (
    <Modal open={open} title="Add Sender Identity" onClose={onClose}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        {mutation.isError && <div className="form-error">{errorMessage(mutation.error)}</div>}
        <Field label="Sender email">
          <input
            className="field__input"
            type="email"
            value={form.senderEmail}
            required
            onChange={(e) => setForm({ ...form, senderEmail: e.target.value })}
          />
        </Field>
        <Field label="Display name" hint="Optional">
          <input
            className="field__input"
            value={form.senderDisplayName ?? ''}
            onChange={(e) => setForm({ ...form, senderDisplayName: e.target.value || undefined })}
          />
        </Field>
        <Field label="Mailbox type">
          <select
            className="field__select"
            value={form.senderType}
            onChange={(e) =>
              setForm({ ...form, senderType: e.target.value as CreateSenderInput['senderType'] })
            }
          >
            {SENDER_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Adding…' : 'Add sender'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
